/**
 * LobbyScreen for Poker Reborn
 *
 * Main hub where players see their profile and choose game types.
 * Rich's living room!
 *
 * Displays game type cards (Cash, Fast-Fold, SNG) from the LOBBY_CARDS
 * descriptor list. Each card is powered by managers.ts config data.
 *
 * @see docs/screens/lobby/README.md - Layout, game type selection, navigation
 * @see src/config/lobbyCards.ts - Card descriptor list
 * @see src/config/managers.ts - Manager data source
 */

import { Application, Container, Graphics } from 'pixi.js';
import { TapperScroller } from '@/components/common/TapperScroller';
import { LobbyBar, LobbyFooter, GameTypeCard, CustomPlayWizard } from '@/components/lobby';
import { SlidePanel } from '@/components/common';
import { lobbyController } from '@/controllers/LobbyController';
import { appController } from '@/controllers/AppController';
import { getScreenManager } from './ScreenManager';
import { LOBBY_CARDS } from '@/config/lobbyCards';
import { inboxService } from '@/services/InboxService';
import { dailyBonusService } from '@/services/DailyBonusService';
import { xpService } from '@/services/XpService';
import { accountService } from '@/services/AccountService';
import { InboxPopup } from '@/components/popups/InboxPopup';
import { BonusPopup } from '@/components/popups/BonusPopup';
import { LevelUpPopup } from '@/components/popups/LevelUpPopup';
import { MyProfilePopup } from '@/components/popups/MyProfilePopup';
import { AvatarSelectPopup } from '@/components/popups/AvatarSelectPopup';
import { avatarService } from '@/services/AvatarService';
import { toggleFullscreen } from '@/utils/fullscreen';
import type { InboxState } from '@/services/InboxService';
import type { DailyBonusStatus } from '@/services/DailyBonusService';
import type { LevelUpData } from '@/services/XpService';
import type { GameType, ManagerConfig } from '@/config/managers';

// =============================================================================
// LAYOUT CONFIGURATION
// =============================================================================

const LOBBY_LAYOUT = {
  /** Gap between LobbyBar bottom and TapperScroller top */
  barToScrollerGap: 32,

  /** Gap between TapperScroller bottom and LobbyFooter top */
  scrollerToFooterGap: 16,

  /** Minimum TapperScroller height */
  minScrollerHeight: 100,

  /** Card dimensions */
  cardWidth: 240,
  cardHeight: 280,

  /** Gap between cards in scroller */
  cardGap: 20,
};

// Store app reference for rotation demo (set by ScreenManager)
let pixiApp: Application | null = null;

/** Set the PixiJS app reference for rotation demo */
export function setPixiAppForRotation(app: Application): void {
  pixiApp = app;
}

// =============================================================================
// LOBBY SCREEN
// =============================================================================

export class LobbyScreen extends Container {
  private background: Graphics;
  private lobbyBar: LobbyBar;
  private lobbyFooter: LobbyFooter;
  private tapperScroller: TapperScroller;
  private gameTypeCards: GameTypeCard[] = [];
  private currentRotation: number = 0;
  private activePopup: (CustomPlayWizard | InboxPopup | BonusPopup | LevelUpPopup | MyProfilePopup | AvatarSelectPopup | SlidePanel) | null = null;

  private screenWidth: number;
  private screenHeight: number;

  constructor(screenWidth: number, screenHeight: number) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create lobby bar (top navigation)
    this.lobbyBar = new LobbyBar(screenWidth, screenHeight);
    this.lobbyBar.onFullscreen = () => { this.handleFullscreen(); };
    this.lobbyBar.onRotate = () => { this.handleRotate(); };
    this.lobbyBar.onSettings = () => { this.handleSettings(); };
    this.lobbyBar.onProfileClick = () => { this.showMyProfilePopup(); };
    this.addChild(this.lobbyBar);

    // Calculate scroller dimensions (below bar)
    const barHeight = this.lobbyBar.getBarHeight();
    const scrollerY = barHeight + LOBBY_LAYOUT.barToScrollerGap;
    const scrollerHeight = LOBBY_LAYOUT.cardHeight + 40; // Card + padding

    // Create tapper scroller for game type cards
    this.tapperScroller = new TapperScroller({
      width: screenWidth,
      height: scrollerHeight,
      tapperWidth: LOBBY_LAYOUT.cardWidth,
      gap: LOBBY_LAYOUT.cardGap,
      snapMode: 'center',
      friction: 0.95,
    });
    this.tapperScroller.y = scrollerY;
    this.addChild(this.tapperScroller);

    // Create lobby footer (bottom navigation)
    this.lobbyFooter = new LobbyFooter(screenWidth, screenHeight);
    this.lobbyFooter.onHelp = () => { this.handleHelp(); };
    this.lobbyFooter.onFriends = () => { console.log('Friends clicked'); };
    this.lobbyFooter.onBonus = () => { this.handleBonus(); };
    this.lobbyFooter.onInbox = () => { this.handleInbox(); };
    this.addChild(this.lobbyFooter);

    // Wire inbox badge updates
    inboxService.on('inboxUpdated', this.onInboxUpdated);

    // Wire daily bonus badge updates
    dailyBonusService.on('statusUpdated', this.onBonusStatusUpdated);

    // Request inbox state after auth (fires inboxUpdated -> badge)
    inboxService.requestInbox();

    // Request daily bonus status (fires statusUpdated -> badge)
    dailyBonusService.requestStatus();

    // Wire XP level-up events
    xpService.on('leveledUp', this.onLevelUp);

    // Request XP status after auth
    xpService.requestStatus();

    // Create game type cards from LOBBY_CARDS descriptors
    for (const descriptor of LOBBY_CARDS) {
      if (descriptor.type === 'game' && descriptor.gameType) {
        const card = new GameTypeCard({
          gameType: descriptor.gameType,
          title: descriptor.title,
          subtitle: descriptor.subtitle,
          width: LOBBY_LAYOUT.cardWidth,
          height: LOBBY_LAYOUT.cardHeight,
        });

        // Wire up callbacks
        const gameType = descriptor.gameType;
        card.onPlayNowSpectator = () => { void this.handlePlayNow(gameType, 'spectator'); };
        card.onPlayNowAutoBuyIn = () => { void this.handlePlayNow(gameType, 'auto-buyin'); };
        card.onPlayNowAutoSeat = () => { void this.handlePlayNow(gameType, 'auto-seat'); };
        card.onCustomPlay = () => { this.handleCustomPlay(gameType); };

        this.tapperScroller.addTapper(card);
        this.gameTypeCards.push(card);

        // Lock game types that aren't available yet
        if (gameType === 'fastfold' || gameType === 'sng') {
          card.setLocked(true);
        }
      }
      // Future: handle descriptor.type === 'promo', 'jump', etc.
    }

    // Initial layout
    this.layout();
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Resize handler */
  resize(screenWidth: number, screenHeight: number): void {
    // When rotated 90°, swap dimensions for layout
    if (this.currentRotation === 90 && pixiApp) {
      const { width: rawWidth, height: rawHeight } = pixiApp.screen;
      if (screenWidth === rawWidth && screenHeight === rawHeight) {
        screenWidth = rawHeight;
        screenHeight = rawWidth;
      }
      pixiApp.stage.x = rawWidth;
    }

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Update component sizes
    this.lobbyBar.resize(screenWidth, screenHeight);
    this.lobbyFooter.resize(screenWidth, screenHeight);
    this.activePopup?.resize(screenWidth, screenHeight);

    this.layout();
  }

  /**
   * Called by ScreenManager every time the lobby becomes the active screen.
   * Re-fetches live data so the UI is up-to-date after returning from Room/Loading.
   */
  refresh(): void {
    xpService.requestStatus();
    accountService.requestBalance();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private layout(): void {
    const { screenWidth: w, screenHeight: h } = this;

    // Draw background
    this.drawBackground();

    // Get bar heights
    const barHeight = this.lobbyBar.getBarHeight();
    const footerHeight = this.lobbyFooter.getBarHeight();

    // Calculate available space for scroller (dynamic)
    const availableHeight = h
      - barHeight
      - footerHeight
      - LOBBY_LAYOUT.barToScrollerGap
      - LOBBY_LAYOUT.scrollerToFooterGap;

    const scrollerHeight = Math.max(availableHeight, LOBBY_LAYOUT.minScrollerHeight);
    const scrollerY = barHeight + LOBBY_LAYOUT.barToScrollerGap;

    // Position and resize the tapper scroller
    this.tapperScroller.y = scrollerY;
    this.tapperScroller.resize(w, scrollerHeight);

    // Position footer at bottom
    this.lobbyFooter.y = h - footerHeight;

    // Center cards vertically within the scroller height
    const cardY = (scrollerHeight - LOBBY_LAYOUT.cardHeight) / 2;
    for (const card of this.gameTypeCards) {
      card.y = cardY;
    }
  }

  private drawBackground(): void {
    const { screenWidth: w, screenHeight: h } = this;

    this.background.clear();

    // Dark gradient background
    this.background.rect(0, 0, w, h);
    this.background.fill(0x06060e);

    // Subtle radial glow
    const glowRadius = Math.min(w, h) * 0.8;
    const centerX = w / 2;
    const centerY = h * 0.4;

    for (let i = 20; i >= 0; i--) {
      const radius = (glowRadius * i) / 20;
      const alpha = 0.015 * (20 - i);
      this.background.circle(centerX, centerY, radius);
      this.background.fill({ color: 0x1a1a3e, alpha });
    }
  }

  // ===========================================================================
  // INBOX HANDLERS
  // ===========================================================================

  private onInboxUpdated = (state: InboxState): void => {
    this.lobbyFooter.setInboxBadge(state.unread_count);
  };

  private onBonusStatusUpdated = (status: DailyBonusStatus): void => {
    this.lobbyFooter.setBonusBadge(status.available);
  };

  private onLevelUp = (data: LevelUpData): void => {
    // Don't show if another popup is active
    if (this.activePopup) return;

    const popup = new LevelUpPopup(this.screenWidth, this.screenHeight, data);
    this.activePopup = popup;
    this.addChild(popup);
    popup.onDismiss = () => {
      if (this.activePopup === popup) this.activePopup = null;
    };
  };

  private handleInbox(): void {
    const popup = new InboxPopup(this.screenWidth, this.screenHeight);
    this.activePopup = popup;
    this.addChild(popup);
    popup.on('destroyed', () => {
      if (this.activePopup === popup) this.activePopup = null;
    });
  }

  private handleBonus(): void {
    const cachedStatus = dailyBonusService.getLastStatus() ?? undefined;
    const popup = new BonusPopup(this.screenWidth, this.screenHeight, cachedStatus);
    this.activePopup = popup;
    this.addChild(popup);
    popup.on('destroyed', () => {
      if (this.activePopup === popup) this.activePopup = null;
    });
  }

  private showMyProfilePopup(): void {
    // Close any existing popup
    if (this.activePopup) {
      this.activePopup.destroy();
      this.activePopup = null;
    }

    const popup = new MyProfilePopup(this.screenWidth, this.screenHeight);
    this.activePopup = popup;
    this.addChild(popup);
    popup.onDismiss = () => {
      if (this.activePopup === popup) this.activePopup = null;
    };
    popup.onChangeAvatar = () => {
      popup.destroy();
      this.showAvatarSelectPopup();
    };
  }

  private showAvatarSelectPopup(): void {
    const currentAvatarId = avatarService.getAvatarId();
    const popup = new AvatarSelectPopup(this.screenWidth, this.screenHeight, currentAvatarId);
    this.activePopup = popup;
    this.addChild(popup);
    popup.onDismiss = () => {
      if (this.activePopup === popup) this.activePopup = null;
    };
  }

  // ===========================================================================
  // SLIDE PANEL HANDLERS
  // ===========================================================================

  private handleHelp(): void {
    if (this.activePopup) return;

    const panel = new SlidePanel({
      screenWidth: this.screenWidth,
      screenHeight: this.screenHeight,
      title: 'Help',
    });
    this.activePopup = panel;
    this.addChild(panel);
    panel.onDismiss = () => {
      if (this.activePopup === panel) this.activePopup = null;
    };
  }

  // ===========================================================================
  // GAME TYPE CARD HANDLERS
  // ===========================================================================

  private async handlePlayNow(
    gameType: GameType,
    mode: 'spectator' | 'auto-buyin' | 'auto-seat',
  ): Promise<void> {
    // Find the card for loading state
    const card = this.gameTypeCards.find((c) => c.gameType === gameType);
    card?.setLoading(true);

    try {
      await lobbyController.playNow(gameType, mode);
    } finally {
      card?.setLoading(false);
    }
  }

  private handleCustomPlay(gameType: GameType): void {
    const wizard = new CustomPlayWizard(
      gameType,
      this.screenWidth,
      this.screenHeight,
    );

    wizard.onSelectManager = (manager: ManagerConfig) => {
      // Join the selected manager's table with auto-seat mode
      // (player will get buy-in popup at the table)
      void lobbyController.joinManager(manager, 'auto-seat');
    };

    wizard.onCancel = () => {
      // Player cancelled — do nothing
    };

    this.activePopup = wizard;
    this.addChild(wizard);
    wizard.on('destroyed', () => {
      if (this.activePopup === wizard) this.activePopup = null;
    });
  }

  // ===========================================================================
  // UTILITY HANDLERS (unchanged)
  // ===========================================================================

  private handleSettings(): void {
    appController.navigateTo('settings');
  }

  private handleRotate(): void {
    if (!pixiApp) return;

    this.currentRotation = this.currentRotation === 0 ? 90 : 0;

    const screenManager = getScreenManager();
    if (screenManager) {
      screenManager.setRotation(this.currentRotation);
    }

    const stage = pixiApp.stage;
    const { width, height } = pixiApp.screen;

    if (this.currentRotation === 90) {
      stage.rotation = Math.PI / 2;
      stage.x = width;
      stage.y = 0;
      this.lobbyBar.setRotation(90);
      this.resize(height, width);
    } else {
      stage.rotation = 0;
      stage.x = 0;
      stage.y = 0;
      this.lobbyBar.setRotation(0);
      this.resize(width, height);
    }
  }

  private handleFullscreen(): void {
    const isNowFullscreen = toggleFullscreen();
    this.lobbyBar.setFullscreen(isNowFullscreen);
  }
}

// =============================================================================
// FACTORY
// =============================================================================

/** Create a lobby screen */
export function createLobbyScreen(width: number, height: number): LobbyScreen {
  return new LobbyScreen(width, height);
}
